import { useTranslation } from 'react-i18next';
import { XMarkIcon } from '@heroicons/react/24/outline';
import type { DriveEntry, DriveTrashEntry } from '@/lib/drive/driveClient';
import { formatBytes, formatTimestamp } from '@/lib/drive/format';
import { DriveComments } from '@/components/drive/DriveComments';

interface DriveDetailsPanelProps {
  entry: DriveEntry | DriveTrashEntry | null;
  pathLabel: string;
  locale?: string;
  onClose: () => void;
}

/** The right-hand inspector: the selected entry's facts, nothing editable. */
export function DriveDetailsPanel({
  entry,
  pathLabel,
  locale,
  onClose,
}: DriveDetailsPanelProps) {
  const { t } = useTranslation();

  // Trash rows carry no path and no kind — every file that rests there is a file.
  const isTrashRow = entry !== null && !('path' in entry);
  const rows = entry
    ? [
        {
          label: t('drive.detailsKind'),
          value: !isTrashRow && entry.kind === 'folder' ? t('drive.kindFolder') : t('drive.kindFile'),
        },
        ...(entry.mimeType ? [{ label: t('drive.detailsType'), value: entry.mimeType }] : []),
        ...(isTrashRow || entry.kind === 'file'
          ? [{ label: t('drive.size'), value: formatBytes(entry.size) }]
          : []),
        {
          label: t('drive.detailsModified'),
          value: formatTimestamp(
            !isTrashRow && 'updatedAt' in entry ? entry.updatedAt || entry.createdAt : entry.deletedAt,
            locale,
          ),
        },
        ...(!isTrashRow && 'path' in entry
          ? [{ label: t('drive.detailsPath'), value: entry.path }]
          : []),
      ]
    : [];

  return (
    <aside
      className="flex min-h-0 flex-1 flex-col overflow-y-auto rounded-lg border border-border-color bg-card"
      data-tour-id="details-panel"
      aria-label={t('drive.details')}
    >
      <div className="flex flex-shrink-0 items-center justify-between gap-2 border-b border-border-color px-4 py-3">
        <h2 className="truncate text-sm font-semibold text-foreground">{t('drive.details')}</h2>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('drive.detailsClose')}
          className="flex-shrink-0 rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>

      {entry ? (
        <div className="flex min-h-0 flex-1 flex-col">
          <dl className="space-y-3 px-4 py-4 text-sm">
            {rows.map((row) => (
              <div key={row.label} className="min-w-0">
                <dt className="text-xs uppercase tracking-wide text-muted-foreground">{row.label}</dt>
                <dd className="truncate whitespace-nowrap text-foreground" title={row.value}>
                  {row.value}
                </dd>
              </div>
            ))}
            {!isTrashRow && entry && (
              <div className="min-w-0">
                <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                  {t('drive.detailsLocation')}
                </dt>
                <dd className="truncate whitespace-nowrap text-muted-foreground" title={pathLabel}>
                  {pathLabel}
                </dd>
              </div>
            )}
          </dl>
          {!isTrashRow && entry && entry.kind === 'file' && <DriveComments entry={entry} />}
        </div>
      ) : (
        <p className="px-4 py-6 text-sm text-muted-foreground">{t('drive.noSelection')}</p>
      )}
    </aside>
  );
}
